import { useCallback, useRef, useState } from "react";

import {
  ERROR_AUTH_SIGN_IN_CONSUME_EXPIRED,
  ERROR_AUTH_SIGN_IN_CONSUME_INVALID_REQUEST,
  ERROR_AUTH_SIGN_IN_REGISTER_EXPIRED,
  ERROR_AUTH_SIGN_IN_REGISTER_INVALID_REQUEST,
} from "~/_constants/errors";
import { EAccount } from "~/_enums/EAccount";
import { ECode } from "~/_enums/ECode";
import { postAuthSignInRequests } from "~/api-versioned/Auth/postAuthSignInRequests";
import { postAuthSignInRequestsRegister } from "~/api-versioned/Auth/postAuthSignInRequestsRegister";
import { useTranslation } from "~/hooks/useTranslation";
import { ENameSpace } from "~/i18n/@types/types";
import { useAlert } from "~/modules/Common/Overlay";
import { EPathname } from "~/_enums/EPathname";
import { buildQuery } from "~/_utils/calibrator/urlCalibrator";
import { assert, assertIsDefined } from "~/_utils/assert";
import { postAuthSignInRequestsConsume } from "~/api-versioned/Auth/postAuthSignInRequestsConsume";
import { useElectronOpenUrl } from "~/router/hooks/useElectronOpenUrl";
import { EDesktopAppDeeplink } from "~/_enums/EDesktopAppDeeplink";
import { TAuthSigninRequestsConsumeSuccessData } from "~/_types/TAuthRequestsConsumeSuccessData";
import { env } from "~/env";

const oauthRedirectPathnames = {
  [EAccount.Apple]: EPathname.OAuthAppleSigninCallback,
  [EAccount.Google]: EPathname.OAuthGoogleSigninCallback,
};

const oauthSetupPathnames = {
  [EAccount.Apple]: EPathname.OAuthAppleSigninSetup,
  [EAccount.Google]: EPathname.OAuthGoogleSigninSetup,
};

export const useOAuthSignin = (
  onSuccess: (consumeData: TAuthSigninRequestsConsumeSuccessData) => void,
) => {
  const { openAlert } = useAlert();
  const { t } = useTranslation([ENameSpace.Common]);
  const [isConsuming, setIsConsuming] = useState(false);
  const requestResponseDataRef = useRef<{
    requestId: string;
    requestKey: string;
  } | null>(null);

  const handleConsume = useCallback(
    async (requestId: string, requestKey: string) => {
      try {
        setIsConsuming(true);

        // 4. 인증 요청 사용
        const consumeResponse = await postAuthSignInRequestsConsume({
          authRequestId: requestId,
          key: requestKey,
        });
        if (consumeResponse.code !== ECode.Success) {
          if (consumeResponse.code === "010") {
            throw new Error(ERROR_AUTH_SIGN_IN_CONSUME_EXPIRED);
          } else if (consumeResponse.code === "003") {
            throw new Error(ERROR_AUTH_SIGN_IN_CONSUME_INVALID_REQUEST);
          }

          throw new Error();
        }

        onSuccess(consumeResponse.data);
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === ERROR_AUTH_SIGN_IN_CONSUME_INVALID_REQUEST
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.TheValidityPeriodHasExpired`,
            ),
          });
        } else if (
          error instanceof Error &&
          error.message === ERROR_AUTH_SIGN_IN_CONSUME_EXPIRED
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.ItSAnInvalidRequest`,
            ),
          });
        } else {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.AccountLinkingFailed`,
            ),
          });
        }
      } finally {
        setIsConsuming(false);
      }
    },
    [onSuccess, openAlert, t],
  );

  useElectronOpenUrl((data) => {
    if (data.url === EDesktopAppDeeplink.AuthSigninConsume) {
      assertIsDefined(requestResponseDataRef.current);
      handleConsume(
        requestResponseDataRef.current.requestId,
        requestResponseDataRef.current.requestKey,
      );
    }
  });

  const handleOAuthSignin = useCallback(
    async (account: typeof EAccount.Google | typeof EAccount.Apple) => {
      try {
        // 1. 인증 요청 생성
        const requestResponse = await postAuthSignInRequests();
        if (requestResponse.code !== ECode.Success) {
          throw new Error();
        }
        requestResponseDataRef.current = {
          requestId: requestResponse.data.id,
          requestKey: requestResponse.data.key,
        };

        // 2. 인증 요청 데이터 등록
        const registerResponse = await postAuthSignInRequestsRegister({
          authRequestId: requestResponse.data.id,
          key: requestResponse.data.key,
          payload: {
            account,
            oauthRedirectUrl:
              window.location.origin + oauthRedirectPathnames[account],
          },
        });
        if (registerResponse.code !== ECode.Success) {
          if (registerResponse.code === "003") {
            throw new Error(ERROR_AUTH_SIGN_IN_REGISTER_INVALID_REQUEST);
          } else if (registerResponse.code === "010") {
            throw new Error(ERROR_AUTH_SIGN_IN_REGISTER_EXPIRED);
          }

          throw new Error();
        }

        assert(
          registerResponse.data.account === EAccount.Google ||
            registerResponse.data.account === EAccount.Apple,
        );
        const url = `${oauthSetupPathnames[account]}?${buildQuery({
          requestId: requestResponse.data.id,
          requestKey: requestResponse.data.key,
          authorizationUrl: registerResponse.data.authorizationUrl,
          fromDesktopApp: env.IS_DESKTOP_APP ? 1 : undefined,
        })}`;

        window.onAuthVerifySuccess = () =>
          handleConsume(requestResponse.data.id, requestResponse.data.key);
        window.open(url);
      } catch (error) {
        if (
          error instanceof Error &&
          error.message === ERROR_AUTH_SIGN_IN_REGISTER_INVALID_REQUEST
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.ItSAnInvalidRequest`,
            ),
          });
        } else if (
          error instanceof Error &&
          error.message === ERROR_AUTH_SIGN_IN_REGISTER_EXPIRED
        ) {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.TheValidityPeriodHasExpired`,
            ),
          });
        } else {
          openAlert({
            text: t(
              `${ENameSpace.Common}:OAuth.ErrorMessage.AccountLinkingFailed`,
            ),
          });
        }
      }
    },
    [handleConsume, openAlert, t],
  );

  const handleGoogleSignin = useCallback(() => {
    handleOAuthSignin(EAccount.Google);
  }, [handleOAuthSignin]);

  const handleAppleSignin = useCallback(() => {
    handleOAuthSignin(EAccount.Apple);
  }, [handleOAuthSignin]);

  return {
    handleGoogleSignin,
    handleAppleSignin,
    isConsuming,
  };
};
